import { DocumentationAreaNotFoundError } from "src/contexts/documentationArea/domain/errors/DocumentationAreaNotFoundError";
import { DocumentationArea } from "src/contexts/documentationArea/domain/DocumentationArea";
import { IdentityVerificationOrderFileData } from "../uploadFiles/UploadFilesData";
import { useAuth } from "../useAuth";
import { useDependencies } from "../useDependencies";
import { useDocumentationArea } from "../useDocumentationArea";
import { useDocumentationAreaMutation } from "../useDocumentationAreaMutation";
import { IdentityVerificationDocumentSides } from "src/contexts/identityVerification/domain/IdentityVerificationDocumentSides";

export const useUploadIdentityVerificationDocuments = () => {
  const { token } = useAuth();
  const { documentationArea } = useDocumentationArea();
  const { documentationAreaController } = useDependencies();

  if (!documentationArea) throw new DocumentationAreaNotFoundError();

  const mutationFn = async (data: IdentityVerificationOrderFileData) => {
    return await documentationAreaController.patchActorIDVOrder({
      accessToken: token,
      documentationAreaId: documentationArea.getId(),
      actorId: documentationArea.getActor().getId(),
      orderId: data.orderId,
      frontFile: data.side === IdentityVerificationDocumentSides.FRONT ? data.file : undefined,
      backFile: data.side === IdentityVerificationDocumentSides.BACK ? data.file : undefined,
    });
  };

  const optimisticUpdate = (
    data: IdentityVerificationOrderFileData,
    documentationArea: DocumentationArea,
  ) => {
    // works with one actor

    const identityVerificationOrder = documentationArea
      .getActor()
      .getIdentityVerificationOrders()
      .find((idv) => idv.getId().toString() === data.orderId);

    const docArea = new DocumentationArea({
      id: documentationArea.getId(),
      type: documentationArea.getType(),
      actor: documentationArea.getActor(),
      availableActors: documentationArea.getAvailableActors(),
      locale: documentationArea.getLocale(),
    });

    if (!identityVerificationOrder) return docArea;

    identityVerificationOrder.setSideLoading(data.side);

    return docArea;
  };

  const { mutate, isSuccess, isError } = useDocumentationAreaMutation<
    string,
    IdentityVerificationOrderFileData
  >({
    mutationFn,
    optimisticUpdate,
  });

  return {
    mutate,
    optimisticUpdate,
    isSuccess,
    isError,
  };
};
